import { AssignmentProps } from "@/interfaces/assignment_types"
import { useState, useEffect } from "react"
import { CardBase } from "./Utils/CardHelper"
import { CardDropdownAssignment } from "./Utils/CardDropdown"
import { markAssignmentAsClosed } from "@/api/assignment"
import { Divider } from "@/components/misc"
import { CategoryIcon } from "./Utils/CategoryIcon"
import { NotesSection } from "../ProjectProgress/Utils/NotesSection"
import { ProgressBar } from "./Utils/ProgressBar"

type AssignmentCardProps = {
    assignment: AssignmentProps,
    isNew: boolean,
    onView: () => void
}
/**
 * TaskCard Component
 * 
 * @param task - The task to be displayed
 * @param isNew - Whether the task is new
 * @param onView - This will turn isNew to false when the task is viewed (or seen)
 * @returns A card displaying the task's details
 * 
 * TODO:
 * - Add the person who created the task
 */
export function AssignmentCard ({assignment, isNew, onView} : AssignmentCardProps) {
    // This is used because onView would make isNew false immediately.
    // So we need to use a local state to prevent unnecessary re-renders
    const [isNewLocal, setIsNewLocal] = useState(isNew)
    const [progressColor, setProgressColor] = useState('bg-white')

    // This is used to encode the task_id so that it can be used in the URL
    // So cases where the task id has slashes or other special characters can be handled
    const encodedID = encodeURIComponent(assignment.id ?? '')

    const handleMark = async () => {
        if (confirm("Are you sure you want to mark this task as completed?")) {
            if (assignment.id) {
                const response = await markAssignmentAsClosed(assignment.id)
                if (response === 200) {
                    window.location.reload()
                }
            } else {
                alert("Assignment ID is not defined")
            }
        }
    }

    useEffect(() => {
        if (isNewLocal) {
            setIsNewLocal(true)
            onView()
        }

        if (assignment.completion_percentage > 75) {
            setProgressColor('bg-green-500')
        } else if (assignment.completion_percentage >= 50) {
            setProgressColor('bg-yellow-200')
        } else {
            setProgressColor('bg-red-500')
        }
    }, [])

    return (
        <CardBase 
            isNew={isNewLocal} 
            hoverChildren={<CardDropdownAssignment
                edit_assignment_route={`/assignments/update_assignment/${encodedID}`} 
                mark_as_completed_fn={handleMark}
            />}
        >
            {/*assignment.pinged && <PingedCardAlert />*/}
            <div className="flex flex-row items-center">
                <CategoryIcon category={assignment.category} />
                <h5 className='font-bold'>{assignment.title}</h5> 
            </div>

            <Divider />

            <NotesSection notes={assignment.description ?? ''} />

            <div className="flex flex-row justify-between">
                <p className="text-red-800 text-sm dark:text-red-400">Due: {assignment.due_date}</p>
                <p className="text-slate-800 text-sm dark:text-slate-100">Created by: {assignment.assigned_by}</p>
            </div>
            
            <ProgressBar progressColor={progressColor} completionPercentage={assignment.completion_percentage} />
        </CardBase>
    )
}